import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Heart, Brain, Activity } from 'lucide-react'

interface Etapa3Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

const COMORBIDADES_CATEGORIAS = {
  cardiovascular: {
    nome: 'Cardiovascular',
    icon: Heart,
    condicoes: ['hipertensao', 'insuficiencia_cardiaca', 'arritmia', 'infarto', 'angina', 'nenhuma_das_opcoes']
  },
  neurologico: {
    nome: 'Neurológico',
    icon: Brain,
    condicoes: ['avc', 'alzheimer', 'parkinson', 'demencia', 'epilepsia', 'nenhuma_das_opcoes']
  },
  psiquiatrico: {
    nome: 'Psiquiátrico',
    icon: Activity,
    condicoes: ['depressao', 'ansiedade', 'bipolaridade', 'esquizofrenia', 'nenhuma_das_opcoes']
  },
  metabolico: {
    nome: 'Metabólico',
    icon: Activity,
    condicoes: ['diabetes', 'obesidade', 'colesterol_alto', 'tireoide', 'nenhuma_das_opcoes']
  },
  respiratorio: {
    nome: 'Respiratório',
    icon: Activity,
    condicoes: ['asma', 'dpoc', 'bronquite', 'apneia', 'nenhuma_das_opcoes']
  },
  ortopedico: {
    nome: 'Ortopédico',
    icon: Activity,
    condicoes: ['osteoporose', 'artrite', 'artrose', 'fraturas', 'nenhuma_das_opcoes']
  }
}

export function Etapa3HistoricoClinico({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa3Props) {
  const handleComorbidadeChange = (categoria: string, condicao: string, possui: boolean) => {
    const currentComorbidades = formData.comorbidades || []
    const existingIndex = currentComorbidades.findIndex(
      (c: any) => c.categoria === categoria && c.condicao === condicao
    )

    let newComorbidades
    if (possui) {
      if (existingIndex >= 0) {
        newComorbidades = currentComorbidades.map((c: any, index: number) =>
          index === existingIndex ? { ...c, possui: true } : c
        )
      } else {
        newComorbidades = [...currentComorbidades, { categoria, condicao, possui: true }]
      }
    } else {
      if (existingIndex >= 0) {
        newComorbidades = currentComorbidades.map((c: any, index: number) =>
          index === existingIndex ? { ...c, possui: false, detalhes: '' } : c
        )
      } else {
        newComorbidades = [...currentComorbidades, { categoria, condicao, possui: false }]
      }
    }

    onChange('comorbidades', newComorbidades)
  }

  const handleDetalhesChange = (categoria: string, condicao: string, detalhes: string) => {
    const currentComorbidades = formData.comorbidades || []
    const newComorbidades = currentComorbidades.map((c: any) =>
      c.categoria === categoria && c.condicao === condicao
        ? { ...c, detalhes }
        : c
    )
    onChange('comorbidades', newComorbidades)
  }

  const getComorbidadeValue = (categoria: string, condicao: string) => {
    const comorbidade = (formData.comorbidades || []).find(
      (c: any) => c.categoria === categoria && c.condicao === condicao
    )
    return comorbidade?.possui || false
  }

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar comorbidades
    const categoriasSemSelecao: string[] = []
    Object.keys(COMORBIDADES_CATEGORIAS).forEach(categoriaKey => {
      const categoria = COMORBIDADES_CATEGORIAS[categoriaKey as keyof typeof COMORBIDADES_CATEGORIAS]
      const temSelecao = categoria.condicoes.some(condicao => getComorbidadeValue(categoriaKey, condicao))
      if (!temSelecao) {
        categoriasSemSelecao.push(categoria.nome)
      }
    })

    if (categoriasSemSelecao.length > 0) {
      errors.push(`Selecione pelo menos uma opção em: ${categoriasSemSelecao.join(', ')}`)
    }

    // Validar internações
    if (!(formData.sem_internacoes || formData.internacoes)) {
      errors.push('Informações sobre internações')
    }

    // Validar quedas
    if (!(formData.sem_quedas || formData.quedas)) {
      errors.push('Informações sobre quedas')
    }

    // Validar alergias
    if (!(formData.sem_alergias || formData.alergias)) {
      errors.push('Informações sobre alergias')
    }

    // Validar declaração de veracidade
    if (!formData.veracidade_etapa3) {
      errors.push('Declaração de veracidade')
    }

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  const getComorbidadeDetalhes = (categoria: string, condicao: string) => {
    const comorbidade = (formData.comorbidades || []).find(
      (c: any) => c.categoria === categoria && c.condicao === condicao
    )
    return comorbidade?.detalhes || ''
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Histórico Clínico</CardTitle>
          <p className="text-sm text-gray-600">
            Selecione as condições que o idoso(a) possui. Se houver detalhes importantes, adicione nos campos de observação.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(COMORBIDADES_CATEGORIAS).map(([categoriaKey, categoria]) => {
            const Icon = categoria.icon
            return (
              <div key={categoriaKey} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold">{categoria.nome}</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoria.condicoes.map((condicao) => (
                    <div key={condicao} className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`${categoriaKey}-${condicao}`}
                          checked={getComorbidadeValue(categoriaKey, condicao)}
                          onCheckedChange={(checked) => 
                            handleComorbidadeChange(categoriaKey, condicao, checked as boolean)
                          }
                        />
                        <Label 
                          htmlFor={`${categoriaKey}-${condicao}`}
                          className="text-sm font-medium"
                        >
                          {condicao === 'nenhuma_das_opcoes' 
                            ? 'Outra/Nenhuma das opções' 
                            : condicao.charAt(0).toUpperCase() + condicao.slice(1).replace(/_/g, ' ')
                          }
                        </Label>
                      </div>
                      
                      {getComorbidadeValue(categoriaKey, condicao) && (
                        <Textarea
                          placeholder="Detalhes (opcional)"
                          value={getComorbidadeDetalhes(categoriaKey, condicao)}
                          onChange={(e) => 
                            handleDetalhesChange(categoriaKey, condicao, e.target.value)
                          }
                          className="mt-1 text-sm"
                          rows={2}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Informações Adicionais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="internacoes">Internações anteriores</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sem_internacoes"
                  checked={formData.sem_internacoes || false}
                  onCheckedChange={(checked) => onChange('sem_internacoes', checked as boolean)}
                />
                <Label htmlFor="sem_internacoes" className="text-sm font-medium">
                  Sem internações
                </Label>
              </div>
              {!formData.sem_internacoes && (
                <Textarea
                  id="internacoes"
                  placeholder="Descreva internações anteriores, quando e o motivo"
                  value={formData.internacoes || ''}
                  onChange={(e) => onChange('internacoes', e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="quedas">Histórico de quedas</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sem_quedas"
                  checked={formData.sem_quedas || false}
                  onCheckedChange={(checked) => onChange('sem_quedas', checked as boolean)}
                />
                <Label htmlFor="sem_quedas" className="text-sm font-medium">
                  Sem histórico
                </Label>
              </div>
              {!formData.sem_quedas && (
                <Textarea
                  id="quedas"
                  placeholder="Descreva quedas recentes, frequência e consequências"
                  value={formData.quedas || ''}
                  onChange={(e) => onChange('quedas', e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="alergias">Alergias medicamentosas</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sem_alergias"
                  checked={formData.sem_alergias || false}
                  onCheckedChange={(checked) => onChange('sem_alergias', checked as boolean)}
                />
                <Label htmlFor="sem_alergias" className="text-sm font-medium">
                  Sem alergias
                </Label>
              </div>
              {!formData.sem_alergias && (
                <Textarea
                  id="alergias"
                  placeholder="Liste medicamentos e/ou substâncias que causam alergia"
                  value={formData.alergias || ''}
                  onChange={(e) => onChange('alergias', e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e 
              foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade_etapa3"
                checked={formData.veracidade_etapa3 || false}
                onCheckedChange={(checked) => onChange('veracidade_etapa3', checked as boolean)}
                className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
              />
              <Label htmlFor="veracidade_etapa3" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
